import { get, set, del, keys, createStore } from 'idb-keyval';
import { Draft } from '../core/models';
import { Store, StoreError, Unsubscribe } from './Store';

const STORE_NAME = 'litewrite-drafts';
const customStore = createStore(STORE_NAME, 'drafts');

export class IdbStore implements Store {
  private listeners: Set<() => void> = new Set();

  async get(id: string): Promise<Draft | null> {
    try {
      const draft = await get<Draft>(id, customStore);
      return draft || null;
    } catch (error) {
      throw new StoreError(
        `Failed to get draft ${id}`,
        'get',
        error
      );
    }
  }

  async list(): Promise<Draft[]> {
    try {
      const allKeys = await keys<string>(customStore);
      const drafts = await Promise.all(
        allKeys.map((key: string) => get<Draft>(key, customStore))
      );
      return drafts
        .filter((draft: Draft | undefined): draft is Draft => draft !== undefined)
        .sort((a: Draft, b: Draft) => b.updated - a.updated);
    } catch (error) {
      throw new StoreError(
        'Failed to list drafts',
        'list',
        error
      );
    }
  }

  async put(draft: Draft): Promise<void> {
    try {
      await set(draft.id, draft, customStore);
      this.notifyListeners();
    } catch (error) {
      throw new StoreError(
        `Failed to save draft ${draft.id}`,
        'put',
        error
      );
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await del(id, customStore);
      this.notifyListeners();
    } catch (error) {
      throw new StoreError(
        `Failed to delete draft ${id}`,
        'remove',
        error
      );
    }
  }

  onChange(callback: () => void): Unsubscribe {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }
} 